import { useEffect, useState } from 'react';
import { formatPrice, formatPriceRange } from '../lib/format';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { getListings, getNeighbourhood, trackEvent } from '../lib/api';
import { ListingCard, ListingCardSkeleton } from '../components/ListingCard';
import { PoweredByRealtor, CreaDisclaimer } from '../components/CreaCompliance';
import SEO from '../components/SEO';

export default function NeighbourhoodListings() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [neighbourhood, setNeighbourhood] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const type = searchParams.get('type') || '';
  const sort = searchParams.get('sort') || 'newest';
  const page = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    trackEvent({ event_type: 'page_view', page: `neighbourhood_listings_${slug}` });
    getNeighbourhood(slug).then(r => setNeighbourhood(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    const params = { neighbourhood: slug, page, limit: 24, sort };
    if (type) params.type = type;
    getListings(params)
      .then(r => { setListings(r.data.listings || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug, type, sort, page]);

  const updateFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.set('page', '1');
    setSearchParams(p);
  };

  const hoodName = neighbourhood?.name || slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const priceFrom = neighbourhood?.price_bands?.detached?.min > 0 ? neighbourhood.price_bands.detached.min : null;

  return (
    <div className="min-h-screen pt-16" data-testid="neighbourhood-listings-page">
      <SEO
        title={`${total} Homes for Sale in ${hoodName}, Burlington ON`}
        description={`Browse ${total} active MLS® listings in ${hoodName}, Burlington. ${priceFrom ? `Homes from ${formatPrice(priceFrom)}.` : ''} Updated daily. Powered by REALTOR.ca.`}
        canonical={`/neighbourhoods/${slug}/homes-for-sale`}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Neighbourhoods', url: '/neighbourhoods' },
          { name: hoodName, url: `/neighbourhoods/${slug}` },
          { name: 'Homes for Sale', url: `/neighbourhoods/${slug}/homes-for-sale` },
        ]}
      />

      {/* Hero */}
      <section className="bg-white border-b border-slate-200 py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link to={`/neighbourhoods/${slug}`} className="inline-flex items-center text-sm text-slate-400 hover:text-slate-900 mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-1" /> {hoodName} Guide
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <Home className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600">{hoodName}, Burlington</span>
          </div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Homes for Sale in <span className="italic">{hoodName}</span>
          </h1>
          <p className="text-base text-slate-500">
            {total} active listing{total !== 1 ? 's' : ''} &middot; Updated daily
            {neighbourhood?.vibe_summary && <span className="hidden md:inline"> &middot; {neighbourhood.vibe_summary.split('.')[0]}</span>}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Filters */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Select value={type || 'all'} onValueChange={v => updateFilter('type', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[130px] h-9 text-xs rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="detached">Detached</SelectItem>
                <SelectItem value="townhome">Townhome</SelectItem>
                <SelectItem value="condo">Condo</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={v => updateFilter('sort', v)}>
              <SelectTrigger className="w-[130px] h-9 text-xs rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_asc">Price: Low</SelectItem>
                <SelectItem value="price_desc">Price: High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Link to="/homes-for-sale" className="text-xs text-slate-400 hover:text-slate-900 transition-colors">
            All Burlington listings <ArrowRight className="w-3 h-3 inline" />
          </Link>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <ListingCardSkeleton key={i} />)}
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {listings.map(l => <ListingCard key={l.mls_number || l.ddf_id} listing={l} />)}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-slate-400 mb-2">No active listings in {hoodName} right now.</p>
            <p className="text-xs text-slate-400">New listings appear as they're posted to MLS.</p>
            <Link to="/homes-for-sale">
              <Button variant="outline" className="rounded-full mt-4 text-xs">Browse All Burlington</Button>
            </Link>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => updateFilter('page', String(page - 1))} className="rounded-lg h-9"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-xs font-mono text-slate-500">{page} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => updateFilter('page', String(page + 1))} className="rounded-lg h-9"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}

        {/* Neighbourhood context + CTA */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {neighbourhood && (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="text-lg tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>About {hoodName}</h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-3">{neighbourhood.vibe_summary}</p>
              <Link to={`/neighbourhoods/${slug}`} className="text-xs text-emerald-600 hover:text-emerald-700">
                Read the full {hoodName} guide <ArrowRight className="w-3 h-3 inline" />
              </Link>
            </div>
          )}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
            <h3 className="text-lg tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>Is {hoodName} right for you?</h3>
            <p className="text-sm text-slate-500 mb-4">The Buyer Blueprint scores all 21 neighbourhoods against your budget, timeline, and priorities.</p>
            <Link to="/blueprint">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-10 px-6 text-sm font-medium">
                Start Blueprint <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* CREA Compliance strip */}
      <div className="border-t border-slate-100 mt-10 py-6 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          <PoweredByRealtor className="h-8 opacity-60 flex-shrink-0" />
          <CreaDisclaimer className="text-center sm:text-left" />
        </div>
      </div>
    </div>
  );
}
